<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import inboxesAPI from 'dashboard/api/inboxes';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  inboxId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const dialogRef = ref(null);
const isSending = ref(false);
const step = ref(1);
const TOTAL_STEPS = 3;

const storyType = ref('text');
const storyContent = ref('');
const storyCaption = ref('');
const storyBackgroundColor = ref('#25D366');
const storyFont = ref(1);
const storyAudienceAll = ref(true);
const storyRecipients = ref('');

const typeCards = computed(() => [
  {
    value: 'text',
    icon: 'i-lucide-type',
    label: t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.TYPE_TEXT'),
  },
  {
    value: 'image',
    icon: 'i-lucide-image',
    label: t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.TYPE_IMAGE'),
  },
  {
    value: 'video',
    icon: 'i-lucide-video',
    label: t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.TYPE_VIDEO'),
  },
  {
    value: 'audio',
    icon: 'i-lucide-mic',
    label: t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.TYPE_AUDIO'),
  },
]);

const storyFontOptions = [
  { value: 1, label: 'Serif' },
  { value: 2, label: 'Norican' },
  { value: 3, label: 'Bryndan' },
  { value: 4, label: 'Bebas Neue' },
  { value: 5, label: 'Oswald' },
];

// Rough visual stand-ins for WhatsApp's status fonts - the real fonts aren't loaded here, this
// is just enough to give the preview a distinct feel per choice.
const FONT_PREVIEW_CLASSES = {
  1: 'font-serif',
  2: 'font-serif italic',
  3: 'italic font-semibold',
  4: 'font-sans font-extrabold uppercase tracking-wide',
  5: 'font-sans font-semibold tracking-tight',
};
const fontPreviewClass = computed(
  () => FONT_PREVIEW_CLASSES[storyFont.value] || ''
);

const isMediaStory = computed(() => storyType.value !== 'text');
const imagePreviewFailed = ref(false);

const isStepValid = computed(() => {
  if (step.value === 2) return !!storyContent.value;
  if (step.value === 3) {
    return storyAudienceAll.value || !!storyRecipients.value;
  }
  return true;
});

const resetForm = () => {
  step.value = 1;
  storyType.value = 'text';
  storyContent.value = '';
  storyCaption.value = '';
  storyBackgroundColor.value = '#25D366';
  storyFont.value = 1;
  storyAudienceAll.value = true;
  storyRecipients.value = '';
  imagePreviewFailed.value = false;
};

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const goNext = () => {
  if (!isStepValid.value) return;
  step.value = Math.min(step.value + 1, TOTAL_STEPS);
};

const goBack = () => {
  step.value = Math.max(step.value - 1, 1);
};

const sendStory = async () => {
  if (!isStepValid.value || isSending.value) return;
  isSending.value = true;
  try {
    await inboxesAPI.sendEvolutionStatus(props.inboxId, {
      type: storyType.value,
      content: storyContent.value,
      caption: isMediaStory.value ? storyCaption.value : undefined,
      background_color: isMediaStory.value
        ? undefined
        : storyBackgroundColor.value,
      font: isMediaStory.value ? undefined : storyFont.value,
      all_contacts: storyAudienceAll.value,
      status_jid_list: storyAudienceAll.value
        ? []
        : storyRecipients.value
            .split(',')
            .map(number => number.trim())
            .filter(Boolean),
    });
    useAlert(t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.SEND_SUCCESS'));
    closeDialog();
  } catch (error) {
    useAlert(t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.SEND_ERROR'));
  } finally {
    isSending.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog ref="dialogRef" width="2xl" @close="resetForm">
    <div class="flex flex-col gap-6">
      <div class="flex items-center justify-between">
        <span class="text-base font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.TITLE') }}
        </span>
        <div class="flex items-center gap-1.5">
          <span
            v-for="dot in TOTAL_STEPS"
            :key="dot"
            class="rounded-full transition-all duration-200"
            :class="
              dot === step ? 'w-6 h-1.5 bg-n-brand' : 'w-1.5 h-1.5 bg-n-slate-6'
            "
          />
        </div>
      </div>

      <!-- Step 1: type -->
      <div v-if="step === 1" class="grid grid-cols-2 gap-3">
        <button
          v-for="card in typeCards"
          :key="card.value"
          type="button"
          class="flex flex-col items-center gap-2 p-5 rounded-xl outline outline-1 -outline-offset-1 transition-colors"
          :class="
            storyType === card.value
              ? 'outline-n-brand bg-n-brand/10'
              : 'outline-n-weak hover:bg-n-solid-2'
          "
          @click="storyType = card.value"
        >
          <Icon :icon="card.icon" class="size-6 text-n-slate-12" />
          <span class="text-sm font-medium text-n-slate-12">{{
            card.label
          }}</span>
        </button>
      </div>

      <!-- Step 2: content + live preview -->
      <div v-else-if="step === 2" class="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div class="flex flex-col gap-4">
          <NextInput
            v-if="isMediaStory"
            v-model="storyContent"
            :label="t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.MEDIA_URL_LABEL')"
            :placeholder="
              t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.MEDIA_URL_PLACEHOLDER')
            "
            @update:model-value="imagePreviewFailed = false"
          />
          <TextArea
            v-else
            v-model="storyContent"
            :label="t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.TEXT_LABEL')"
            :placeholder="
              t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.TEXT_PLACEHOLDER')
            "
          />

          <NextInput
            v-if="isMediaStory"
            v-model="storyCaption"
            :label="t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.CAPTION_LABEL')"
          />

          <template v-else>
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.BACKGROUND_LABEL') }}
              </label>
              <ColorPicker v-model="storyBackgroundColor" />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.FONT_LABEL') }}
              </label>
              <NextSelect v-model="storyFont" :options="storyFontOptions" />
            </div>
          </template>
        </div>

        <div class="flex items-center justify-center">
          <div
            class="relative flex flex-col items-center justify-center w-40 h-72 overflow-hidden rounded-2xl shadow-lg outline outline-1 outline-n-weak"
            :style="
              !isMediaStory ? { backgroundColor: storyBackgroundColor } : {}
            "
            :class="isMediaStory ? 'bg-n-slate-3' : ''"
          >
            <template v-if="!isMediaStory">
              <p
                class="px-4 text-center text-white break-words"
                :class="fontPreviewClass"
              >
                {{
                  storyContent ||
                  t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.TEXT_PLACEHOLDER')
                }}
              </p>
            </template>
            <template v-else-if="storyType === 'image'">
              <img
                v-if="storyContent && !imagePreviewFailed"
                :src="storyContent"
                class="object-cover w-full h-full"
                alt=""
                @error="imagePreviewFailed = true"
              />
              <Icon
                v-else
                icon="i-lucide-image"
                class="text-n-slate-10 size-10"
              />
            </template>
            <Icon
              v-else-if="storyType === 'video'"
              icon="i-lucide-play-circle"
              class="text-n-slate-10 size-10"
            />
            <Icon v-else icon="i-lucide-mic" class="text-n-slate-10 size-10" />
            <p
              v-if="isMediaStory && storyCaption"
              class="absolute bottom-0 w-full px-3 py-2 text-xs text-white bg-black/40"
            >
              {{ storyCaption }}
            </p>
          </div>
        </div>
      </div>

      <!-- Step 3: audience -->
      <div v-else class="flex flex-col gap-4">
        <button
          type="button"
          class="flex items-center justify-between p-4 rounded-xl outline outline-1 -outline-offset-1 transition-colors"
          :class="
            storyAudienceAll
              ? 'outline-n-brand bg-n-brand/10'
              : 'outline-n-weak hover:bg-n-solid-2'
          "
          @click="storyAudienceAll = true"
        >
          <div class="text-start">
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.AUDIENCE_ALL.LABEL') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{
                t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.AUDIENCE_ALL.DESCRIPTION')
              }}
            </p>
          </div>
          <Icon
            v-if="storyAudienceAll"
            icon="i-lucide-check"
            class="text-n-brand size-5 shrink-0"
          />
        </button>
        <button
          type="button"
          class="flex items-center justify-between p-4 rounded-xl outline outline-1 -outline-offset-1 transition-colors"
          :class="
            !storyAudienceAll
              ? 'outline-n-brand bg-n-brand/10'
              : 'outline-n-weak hover:bg-n-solid-2'
          "
          @click="storyAudienceAll = false"
        >
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.RECIPIENTS_LABEL') }}
          </p>
          <Icon
            v-if="!storyAudienceAll"
            icon="i-lucide-check"
            class="text-n-brand size-5 shrink-0"
          />
        </button>
        <NextInput
          v-if="!storyAudienceAll"
          v-model="storyRecipients"
          :placeholder="
            t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.RECIPIENTS_PLACEHOLDER')
          "
        />
        <p v-if="!storyAudienceAll" class="text-xs text-n-slate-11">
          {{ t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.RECIPIENTS_HELP_TEXT') }}
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          v-if="step === 1"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="button"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="closeDialog"
        />
        <Button
          v-else
          :label="t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.BACK_BUTTON')"
          variant="faded"
          color="slate"
          type="button"
          @click="goBack"
        />
        <Button
          v-if="step < TOTAL_STEPS"
          :label="t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.NEXT_BUTTON')"
          color="blue"
          type="button"
          :disabled="!isStepValid"
          @click="goNext"
        />
        <Button
          v-else
          :label="t('INBOX_MGMT.EVOLUTION_PROFILE.STORY.SEND_BUTTON')"
          color="blue"
          type="button"
          :is-loading="isSending"
          :disabled="!isStepValid || isSending"
          @click="sendStory"
        />
      </div>
    </template>
  </Dialog>
</template>
